import { Button, css, styled } from 'canoe';
import { connectWithHammock, ENDPOINTS, useActions } from 'hammock';
import React, { useState } from 'react';

import { DocumentPreview, IFrameOverlay } from '@candu-ai/document/preview';
import { useAuthentication } from 'commons/hooks';
import { refresh } from 'commons/window';
import { H3 } from 'styleguide/typography';

import HistoryCard from './HistoryCard';

export const HistoryWrapper = styled.div`
  height: 90vh;
  position: relative;
  width: 300px;
`;

export const HistoryOverflow = styled.div`
  height: calc(100% - 60px);
  padding-right: 11px;
  overflow-x: visible;
  overflow-y: auto;
`;

export const HistoryBackground = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.White};
    height: 100%;
  `}
`;

const TemplateContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL20};
    border-radius: ${theme.borderRadius.regular};
    position: relative;
    z-index: 1;
    height: 660px;

    > div {
      padding: 1em;
      background-color: ${theme.colors.White};
    }
  `}
`;

export const FlexRowContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.White};
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: space-between;
    margin-right: 11px;
    padding: 9px 20px;
  `}
`;

interface Props {
  documentId: number;
  history: any;
}

export const HistoryContainerImpl = ({ history, documentId }: Props) => {
  const [selectedHistory, setSelectedHistory] = useState(history.commits[0].id);

  const { applicationId }: any = useAuthentication();

  const { action } = useActions(ENDPOINTS.documentHistory);
  const restoreVersion = async (contentHashId: string) => {
    await action({ name: 'restoreVersion', query: { contentHashId, documentId } });

    refresh();
  };

  const onRestore = () => {
    const selectedCommit = history.commits.find(({ id }: { id: string }) => id === selectedHistory);
    restoreVersion(selectedCommit.treeHash);
  };

  return (
    <div css={{ display: 'flex' }}>
      <HistoryWrapper>
        <HistoryOverflow>
          <HistoryBackground>
            {history.commits.map((commit: any) => (
              <HistoryCard
                key={commit.id}
                active={commit.id === selectedHistory}
                createdAt={commit.createdAt}
                publishedState={commit.state}
                historyId={commit.id}
                authorId={commit.authorId}
                changeHistory={setSelectedHistory}
              />
            ))}
          </HistoryBackground>
        </HistoryOverflow>
        <FlexRowContainer>
          <Button onClick={onRestore}>Restore Version</Button>
        </FlexRowContainer>
      </HistoryWrapper>
      <div css={{ padding: '1em', backgroundColor: 'white', flex: 1 }}>
        <H3>History</H3>
        <TemplateContainer>
          <IFrameOverlay />
          <DocumentPreview
            hotspotLimit={0}
            checkeredBackdrop={false}
            applicationId={applicationId}
            commitId={selectedHistory ?? ''}
            isSdkPreview
          />
        </TemplateContainer>
      </div>
    </div>
  );
};

export default connectWithHammock(({ documentId }) => ({
  resourceName: 'history',
  endpoint: ENDPOINTS.documentHistory,
  id: documentId,
  staleTime: 0,
}))(HistoryContainerImpl);
